<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Routes, type RouteName } from '@/router/routes'
import { useTutorialAgentStore } from '@/stores/tutorialAgentStore'
import {
  recordTutorialFallbackMatch,
  registerTutorialIdAlias,
  resolveTutorialIdAlias
} from '@/feature/tutorialAgent/tutorialIdAliasStore'
import { recordTutorialActionResolution } from '@/feature/tutorialAgent/tutorialActionResolutionMetrics'
import {
  resolveTutorialActionElement,
  tutorialActionHasTargetHints
} from '@/feature/tutorialAgent/tutorialTargetResolver'
import { REPLAY_SENSITIVE_SIDE_EFFECT_ACTION_TYPES } from '@/feature/tutorialAgent/guidedActionHelpers'

const store = useTutorialAgentStore()
const router = useRouter()
type ActiveAction = typeof store.currentAction
let navigationTimer: ReturnType<typeof setTimeout> | null = null
const targetRect = ref<{ top: number; left: number; width: number; height: number } | null>(null)
const actionPanelRef = ref<HTMLElement | null>(null)
const progressAnimationKey = ref(0)
const progressDurationMs = ref<number | null>(null)
const TARGET_RESOLVE_MAX_RETRIES = 20
const TARGET_RESOLVE_RETRY_MS = 250
const MIN_SIDE_EFFECT_VISIBLE_MS = 900
const ACTION_PANEL_WIDTH = 320
const ACTION_PANEL_HEIGHT = 148
const ACTION_PANEL_GAP = 12
const VIEWPORT_PADDING = 16
const tooltipStyle = computed(() => {
  if (!targetRect.value) {
    return {
      top: '24px',
      right: '24px',
      left: 'auto'
    }
  }
  return {
    top: `${targetRect.value.top + targetRect.value.height + 12}px`,
    left: `${targetRect.value.left}px`
  }
})
const actionPanelStyle = computed(() => {
  if (!targetRect.value) {
    return {
      top: `${VIEWPORT_PADDING}px`,
      right: `${VIEWPORT_PADDING}px`,
      left: 'auto'
    }
  }

  const viewportMinLeft = VIEWPORT_PADDING
  const viewportMaxLeft = window.innerWidth - ACTION_PANEL_WIDTH - VIEWPORT_PADDING
  const clampedLeft = Math.min(
    Math.max(targetRect.value.left, viewportMinLeft),
    Math.max(viewportMinLeft, viewportMaxLeft)
  )

  const minTop = VIEWPORT_PADDING
  const maxTop = window.innerHeight - ACTION_PANEL_HEIGHT - VIEWPORT_PADDING
  const preferredBelow = targetRect.value.top + targetRect.value.height + ACTION_PANEL_GAP
  const preferredAbove = targetRect.value.top - ACTION_PANEL_HEIGHT - ACTION_PANEL_GAP
  const top = preferredBelow <= maxTop ? preferredBelow : Math.max(minTop, preferredAbove)

  return {
    top: `${top}px`,
    left: `${clampedLeft}px`
  }
})
let retryCount = 0
let retryTimer: ReturnType<typeof setTimeout> | null = null
let resolveMetricActionKey: string | null = null
let resolveMetricAttempts = 0
let resolveMetricRecorded = false
let lockBypassTarget: EventTarget | null = null
const executedSideEffectActionKeys = new Set<string>()

const normalizeToken = (value: string) => value.trim().toLowerCase()

function getMetaString(action: ActiveAction, key: string) {
  if (!action?.meta) return null
  const value = action.meta[key]
  return typeof value === 'string' ? value : null
}

function getMetaNumber(action: ActiveAction, key: string) {
  if (!action?.meta) return null
  const value = action.meta[key]
  return typeof value === 'number' ? value : null
}

function getMetaBoolean(action: ActiveAction, key: string) {
  if (!action?.meta) return null
  const value = action.meta[key]
  return typeof value === 'boolean' ? value : null
}

const getCurrentAliasScopes = () => {
  const scopes: string[] = [router.currentRoute.value.path]
  const routeName = router.currentRoute.value.name
  if (routeName) {
    scopes.push(String(routeName))
  }
  return scopes
}

const escapeForAttributeSelector = (value: string) => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value)
  }
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

const findElementByTutorialIdFallback = (targetId: string) => {
  const candidates = Array.from(document.querySelectorAll<HTMLElement>('[data-tutorial-id]'))
  return candidates.find(element => element.dataset.tutorialId === targetId) ?? null
}

const resolveElementByTutorialId = (targetId: string) => {
  try {
    const escapedId = escapeForAttributeSelector(targetId)
    const exactMatch = document.querySelector<HTMLElement>(`[data-tutorial-id="${escapedId}"]`)
    if (exactMatch) return exactMatch
  } catch {
    // fall through to robust dataset scan when selector parsing fails
  }
  return findElementByTutorialIdFallback(targetId)
}

const resolveElementByAlias = (targetId: string) => {
  const scopes = getCurrentAliasScopes()
  for (const scope of scopes) {
    const resolvedId = resolveTutorialIdAlias({ targetId, scope })
    if (!resolvedId) continue
    const element = resolveElementByTutorialId(resolvedId)
    if (!element) continue
    return element
  }
  return null
}

const resetResolveMetricState = () => {
  resolveMetricActionKey = null
  resolveMetricAttempts = 0
  resolveMetricRecorded = false
}

const getActionResolveMetricKey = (action: ActiveAction) => {
  if (!action) return null
  return [
    store.playbackRunId,
    store.currentIndex,
    action.type,
    action.targetId ?? '',
    getMetaString(action, 'label') ?? '',
    getMetaString(action, 'containerId') ?? ''
  ].join('::')
}

const syncResolveMetricState = (action: ActiveAction) => {
  const nextKey = getActionResolveMetricKey(action)
  if (nextKey === resolveMetricActionKey) return
  resolveMetricActionKey = nextKey
  resolveMetricAttempts = 0
  resolveMetricRecorded = false
}

const recordResolveMetric = (outcome: 'success' | 'failed') => {
  if (resolveMetricRecorded) return
  recordTutorialActionResolution({
    attemptCount: resolveMetricAttempts > 0 ? resolveMetricAttempts : 1,
    outcome
  })
  resolveMetricRecorded = true
}

const resolveTarget = () => {
  const action = store.currentAction
  if (!action || !tutorialActionHasTargetHints(action)) {
    targetRect.value = null
    resetResolveMetricState()
    retryCount = 0
    if (retryTimer) {
      clearTimeout(retryTimer)
      retryTimer = null
    }
    return
  }
  syncResolveMetricState(action)
  resolveMetricAttempts += 1
  const resolvedTarget = resolveTutorialActionElement({
    action,
    currentRoutePath: router.currentRoute.value.path,
    currentRouteName: router.currentRoute.value.name
      ? String(router.currentRoute.value.name)
      : null,
    resolveAlias: ({ targetId, scope }) => resolveTutorialIdAlias({ targetId, scope }),
    registerAlias: ({ sourceId, resolvedId, scope }) =>
      registerTutorialIdAlias({ sourceId, resolvedId, scope })
  })
  if (
    resolvedTarget.strategy === 'alias' ||
    resolvedTarget.strategy === 'route_label_role' ||
    resolvedTarget.strategy === 'container'
  ) {
    recordTutorialFallbackMatch(resolvedTarget.strategy)
  }
  const element = resolvedTarget.element
  if (!element) {
    targetRect.value = null
    if (retryCount < TARGET_RESOLVE_MAX_RETRIES) {
      retryCount += 1
      if (retryTimer) clearTimeout(retryTimer)
      retryTimer = setTimeout(() => resolveTarget(), TARGET_RESOLVE_RETRY_MS)
      return
    }
    recordResolveMetric('failed')
    store.setActionFailed(store.currentIndex, 'Could not find the target element for this step.')
    return
  }
  const rect = element.getBoundingClientRect()
  const isInViewport =
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  if (!isInViewport) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    if (retryTimer) clearTimeout(retryTimer)
    retryTimer = setTimeout(() => resolveTarget(), TARGET_RESOLVE_RETRY_MS)
    return
  }
  recordResolveMetric('success')
  targetRect.value = {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height
  }
  retryCount = 0
  if (retryTimer) {
    clearTimeout(retryTimer)
    retryTimer = null
  }
}

const normalizeRoute = (route: string) => {
  if (route === '/home') {
    return { name: Routes.HOME }
  }
  if (route.startsWith('/')) {
    return { path: route }
  }
  const routeValues = Object.values(Routes)
  if (routeValues.includes(route as RouteName)) {
    return { name: route as RouteName }
  }
  return { path: `/${route}` }
}

const resolveActionElement = (action: ActiveAction) => {
  const resolvedTarget = resolveTutorialActionElement({
    action,
    currentRoutePath: router.currentRoute.value.path,
    currentRouteName: router.currentRoute.value.name
      ? String(router.currentRoute.value.name)
      : null,
    resolveAlias: ({ targetId, scope }) => resolveTutorialIdAlias({ targetId, scope }),
    registerAlias: ({ sourceId, resolvedId, scope }) =>
      registerTutorialIdAlias({ sourceId, resolvedId, scope })
  })
  return resolvedTarget.element
}

const withLockBypassForTarget = <T,>(target: EventTarget | null, run: () => T): T => {
  lockBypassTarget = target
  try {
    return run()
  } finally {
    lockBypassTarget = null
  }
}

const waitForVisibleDwell = async (action: ActiveAction) => {
  const metaDwellMs = getMetaNumber(action, 'minVisibleMs')
  const dwellMs = metaDwellMs && metaDwellMs > 0 ? metaDwellMs : MIN_SIDE_EFFECT_VISIBLE_MS
  await new Promise(resolve => setTimeout(resolve, dwellMs))
}

const buildNavigationConsentMessage = (action: ActiveAction) => {
  const explicitMessage = getMetaString(action, 'confirmMessage')
  if (explicitMessage) return explicitMessage
  if (store.hasUnsavedChanges) return 'You have unsaved changes. Leave this page?'
  if (action?.route) return `Move to ${action.route}?`
  const label = getMetaString(action, 'label')
  if (label) return `Move to ${label}?`
  return 'Move to another section?'
}

const handleNavigationDenied = (action: ActiveAction, message: string) => {
  store.setActionFailed(store.currentIndex, message)
  const shouldStopFlow = action?.meta?.cancelStopsFlow !== false
  if (shouldStopFlow) {
    store.stop()
    return
  }
  store.advanceNow()
}

const getKnownNavigationElements = () => {
  return Array.from(
    document.querySelectorAll<HTMLElement>(
      '[data-tutorial-id^="tutorial.nav."][data-tutorial-label]'
    )
  )
}

const findNavTarget = (
  action: ActiveAction
): { element: HTMLElement | null; reason: string | null } => {
  if (!action) {
    return { element: null, reason: 'Navigation action is missing.' }
  }

  if (action.targetId) {
    if (!action.targetId.startsWith('tutorial.nav.')) {
      return { element: null, reason: 'Navigation target is not a known nav item.' }
    }
    const targetById =
      resolveElementByTutorialId(action.targetId) ?? resolveElementByAlias(action.targetId)
    if (!targetById) {
      return { element: null, reason: 'Navigation target ID was not found in the current nav.' }
    }
    return { element: targetById, reason: null }
  }

  const label = getMetaString(action, 'label')
  if (!label) {
    return { element: null, reason: 'Navigation action is missing route and nav label.' }
  }

  const normalizedLabel = normalizeToken(label)
  const candidates = getKnownNavigationElements()
  const byLabel = candidates.filter(
    element => normalizeToken(element.dataset.tutorialLabel ?? '') === normalizedLabel
  )
  if (byLabel.length === 0) {
    return { element: null, reason: 'Navigation label was not found in known nav items.' }
  }

  const routeHint = getMetaString(action, 'route')
  if (routeHint) {
    const routeScoped = byLabel.filter(element => {
      const targetId = element.dataset.tutorialId
      if (!targetId) return false
      const routeToken = routeHint.trim().toLowerCase()
      return targetId.toLowerCase().includes(routeToken)
    })
    if (routeScoped.length > 0) {
      return { element: routeScoped[0], reason: null }
    }
  }

  return { element: byLabel[0], reason: null }
}

const runActionSideEffects = async () => {
  const action = store.currentAction
  if (!action) return
  if (action.type === 'navigate') {
    try {
      const skipNavigationConsent = getMetaBoolean(action, 'skipNavigationConsent') === true
      if (!skipNavigationConsent) {
        const consentMessage = buildNavigationConsentMessage(action)
        const consentGranted = await store.requestNavigationConsent(consentMessage)
        if (!consentGranted) {
          const deniedMessage = store.hasUnsavedChanges
            ? 'Navigation canceled due to unsaved changes.'
            : 'Navigation canceled by user.'
          handleNavigationDenied(action, deniedMessage)
          return
        }
      }
      if (action.route) {
        const target = normalizeRoute(action.route)
        const resolved = router.resolve(target)
        if (!resolved.matched.length) {
          store.setActionFailed(store.currentIndex, 'Navigation target not allowed.')
          store.advanceNow()
          return
        }
        if (router.currentRoute.value.fullPath === resolved.fullPath) {
          store.advanceNow()
          return
        }
        store.setNavigating(true)
        if (navigationTimer) {
          clearTimeout(navigationTimer)
          navigationTimer = null
        }
        navigationTimer = setTimeout(() => {
          store.setActionFailed(store.currentIndex, 'Navigation timed out.')
          store.setNavigating(false)
          store.advanceNow()
        }, 5000)
        await router.push(target)
        return
      }
      const navTarget = findNavTarget(action)
      if (!navTarget.element) {
        store.setActionFailed(
          store.currentIndex,
          navTarget.reason ?? 'Navigation target not found.'
        )
        store.advanceNow()
        return
      }
      store.setNavigating(true)
      if (navigationTimer) {
        clearTimeout(navigationTimer)
        navigationTimer = null
      }
      navigationTimer = setTimeout(() => {
        store.setActionFailed(store.currentIndex, 'Navigation timed out.')
        store.setNavigating(false)
        store.advanceNow()
      }, 5000)
      withLockBypassForTarget(navTarget.element, () => navTarget.element.click())
    } catch (_error) {
      store.setActionFailed(store.currentIndex, 'Navigation failed.')
    } finally {
      store.setNavigating(false)
    }
    return
  }
  if (action.type === 'scroll' && tutorialActionHasTargetHints(action)) {
    const element = resolveActionElement(action)
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return
  }
  if (action.type === 'click' && tutorialActionHasTargetHints(action)) {
    const element = resolveActionElement(action)
    if (!element) {
      store.setActionFailed(store.currentIndex, 'Could not click the target element.')
      store.advanceNow()
      return
    }
    withLockBypassForTarget(element, () => element.click())
    return
  }
  if (action.type === 'openPanel') {
    const panel = getMetaString(action, 'panel')
    if (panel !== 'input' && panel !== 'output') {
      store.setActionFailed(store.currentIndex, 'Panel type missing for action.')
      store.advanceNow()
      return
    }
    if (!store.handlers.openPanel) {
      store.setActionFailed(store.currentIndex, 'Panel handler unavailable.')
      store.advanceNow()
      return
    }
    store.handlers.openPanel(panel)
    return
  }
  if (action.type === 'openInputSheet') {
    const sheet = getMetaString(action, 'sheet')
    if (
      sheet !== 'upload' &&
      sheet !== 'library' &&
      sheet !== 'content' &&
      sheet !== 'scraper' &&
      sheet !== 'webSearch'
    ) {
      store.setActionFailed(store.currentIndex, 'Input sheet type missing for action.')
      store.advanceNow()
      return
    }
    if (!store.handlers.openInputSheet) {
      store.setActionFailed(store.currentIndex, 'Input sheet handler unavailable.')
      store.advanceNow()
      return
    }
    store.handlers.openInputSheet(sheet)
    return
  }
  if (action.type === 'closeInputSheet') {
    const sheet = getMetaString(action, 'sheet')
    if (
      sheet !== 'upload' &&
      sheet !== 'library' &&
      sheet !== 'content' &&
      sheet !== 'scraper' &&
      sheet !== 'webSearch'
    ) {
      store.setActionFailed(store.currentIndex, 'Input sheet type missing for action.')
      store.advanceNow()
      return
    }
    if (!store.handlers.closeInputSheet) {
      store.setActionFailed(store.currentIndex, 'Input sheet handler unavailable.')
      store.advanceNow()
      return
    }
    store.handlers.closeInputSheet(sheet)
    return
  }
  if (action.type === 'setFormats') {
    const format = getMetaString(action, 'format')
    const formats = format ? [format] : null
    if (!formats || formats.length === 0) {
      store.setActionFailed(store.currentIndex, 'No format provided for action.')
      store.advanceNow()
      return
    }
    if (!store.handlers.setFormats) {
      store.setActionFailed(store.currentIndex, 'Format handler unavailable.')
      store.advanceNow()
      return
    }
    store.handlers.setFormats(formats)
    return
  }
  if (action.type === 'insertDemoFile') {
    if (!store.handlers.insertDemoFile) {
      store.setActionFailed(store.currentIndex, 'Demo file handler unavailable.')
      store.advanceNow()
      return
    }
    try {
      await store.handlers.insertDemoFile()
      await waitForVisibleDwell(action)
      store.advanceNow()
    } catch {
      store.setActionFailed(store.currentIndex, 'Failed to insert demo file.')
      await waitForVisibleDwell(action)
      store.advanceNow()
    }
    return
  }
  if (action.type === 'waitForEditorText') {
    const timeoutMs = getMetaNumber(action, 'timeoutMs') ?? 12000
    const start = Date.now()
    const check = async (): Promise<boolean> => {
      if (store.handlers.hasEditorText?.()) return true
      if (Date.now() - start > timeoutMs) return false
      await new Promise(resolve => setTimeout(resolve, 350))
      return check()
    }
    const ready = await check()
    if (!ready) {
      store.setActionFailed(store.currentIndex, 'Editor text not ready in time.')
    }
    await waitForVisibleDwell(action)
    store.advanceNow()
    return
  }
  if (action.type === 'waitForGeneratedOutput') {
    if (!store.handlers.hasGeneratedOutput) {
      store.setActionFailed(store.currentIndex, 'Generated output handler unavailable.')
      store.advanceNow()
      return
    }
    const timeoutMs = getMetaNumber(action, 'timeoutMs') ?? 25000
    const start = Date.now()
    const check = async (): Promise<boolean> => {
      if (store.handlers.hasGeneratedOutput?.()) return true
      if (Date.now() - start > timeoutMs) return false
      await new Promise(resolve => setTimeout(resolve, 350))
      return check()
    }
    const ready = await check()
    if (!ready) {
      store.setActionFailed(store.currentIndex, 'Generated output not ready in time.')
    }
    await waitForVisibleDwell(action)
    store.advanceNow()
    return
  }
}

const onViewportChange = () => {
  resolveTarget()
}

const isEventInsideActionPanel = (event: MouseEvent) => {
  const target = event.target as Node | null
  if (!target) return false
  return actionPanelRef.value?.contains(target) ?? false
}

const isEventInsideConsentPanel = (event: MouseEvent) => {
  const target = event.target
  if (!(target instanceof HTMLElement)) return false
  return (
    target.closest('[data-tutorial-consent-panel="true"]') !== null ||
    target.closest('[data-tutorial-flow-consent-panel="true"]') !== null
  )
}

const isUiInteractionLocked = () => {
  const action = store.currentAction
  if (!store.isPlaying) return false
  if (!action) return false
  if (getMetaBoolean(action, 'showControls') !== true) return false
  if (getMetaBoolean(action, 'hideControls') === true) return false
  return true
}

const blockUiInteraction = (event: MouseEvent) => {
  if (!isUiInteractionLocked()) return
  if (lockBypassTarget && event.target === lockBypassTarget) return
  if (isEventInsideActionPanel(event)) return
  if (isEventInsideConsentPanel(event)) return
  event.preventDefault()
  event.stopPropagation()
  event.stopImmediatePropagation()
}

const actionHasReplaySensitiveSideEffects = (action: ActiveAction) => {
  if (!action) return false
  return REPLAY_SENSITIVE_SIDE_EFFECT_ACTION_TYPES.has(action.type)
}

const getReplaySideEffectActionKey = (action: ActiveAction, index: number) => {
  if (!action || index < 0) return null
  return `${store.playbackRunId}:${index}:${action.type}`
}

watch(
  () => store.currentAction,
  async () => {
    const action = store.currentAction
    resolveTarget()
    if (!action) return
    const actionIndex = store.currentIndex
    const replaySensitive = actionHasReplaySensitiveSideEffects(action)
    const replayActionKey = replaySensitive
      ? getReplaySideEffectActionKey(action, actionIndex)
      : null
    const alreadyExecuted = replayActionKey
      ? executedSideEffectActionKeys.has(replayActionKey)
      : false
    if (alreadyExecuted) return
    if (action.type === 'navigate') {
      try {
        await runActionSideEffects()
      } finally {
        if (replayActionKey) {
          executedSideEffectActionKeys.add(replayActionKey)
        }
      }
      // Keep navigate steps visible for their configured duration.
      // Advancing immediately after router.push() causes multi-route tours
      // to chain through steps in milliseconds and clear too fast.
      return
    }
    if (action.type === 'scroll' && tutorialActionHasTargetHints(action)) {
      try {
        await runActionSideEffects()
      } finally {
        if (replayActionKey) {
          executedSideEffectActionKeys.add(replayActionKey)
        }
      }
      store.advanceNow()
      return
    }
    if (
      action.type === 'insertDemoFile' ||
      action.type === 'waitForEditorText' ||
      action.type === 'waitForGeneratedOutput'
    ) {
      try {
        await runActionSideEffects()
      } finally {
        if (replayActionKey) {
          executedSideEffectActionKeys.add(replayActionKey)
        }
      }
      return
    }
    try {
      await runActionSideEffects()
    } finally {
      if (replayActionKey) {
        executedSideEffectActionKeys.add(replayActionKey)
      }
    }
  },
  { immediate: true }
)

watch(
  () => store.playbackRunId,
  () => {
    executedSideEffectActionKeys.clear()
  }
)

router.afterEach(() => {
  if (!store.isNavigating) return
  if (store.currentAction?.type !== 'navigate') return
  if (navigationTimer) {
    clearTimeout(navigationTimer)
    navigationTimer = null
  }
  setTimeout(() => {
    if (store.currentAction?.type !== 'navigate') return
    resolveTarget()
  }, 120)
})

onMounted(() => {
  window.addEventListener('resize', onViewportChange)
  window.addEventListener('scroll', onViewportChange, { passive: true })
  document.addEventListener('click', onViewportChange, true)
  document.addEventListener('keyup', onViewportChange, true)
  document.addEventListener('mousedown', blockUiInteraction, true)
  document.addEventListener('mouseup', blockUiInteraction, true)
  document.addEventListener('click', blockUiInteraction, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('scroll', onViewportChange)
  document.removeEventListener('click', onViewportChange, true)
  document.removeEventListener('keyup', onViewportChange, true)
  document.removeEventListener('mousedown', blockUiInteraction, true)
  document.removeEventListener('mouseup', blockUiInteraction, true)
  document.removeEventListener('click', blockUiInteraction, true)
  if (retryTimer) {
    clearTimeout(retryTimer)
    retryTimer = null
  }
  if (navigationTimer) {
    clearTimeout(navigationTimer)
    navigationTimer = null
  }
})

const activeAction = computed(() => store.currentAction)
const hasFlowControls = computed(() => getMetaBoolean(activeAction.value, 'showControls') === true)
const suppressSingleStepGuidedOverlay = computed(() => false)
const hideOverlayControls = computed(() => {
  if (!hasFlowControls.value) return true
  return getMetaBoolean(activeAction.value, 'hideControls') === true
})
const isUiLocked = computed(() => {
  if (!store.isPlaying) return false
  if (!activeAction.value) return false
  if (!hasFlowControls.value) return false
  if (suppressSingleStepGuidedOverlay.value) return false
  if (hideOverlayControls.value) return false
  return true
})
const showStepProgress = computed(() => {
  if (hideOverlayControls.value) return false
  if (!activeAction.value) return false
  return store.isActionAutoAdvance(activeAction.value)
})
const progressAnimationState = computed(() => {
  return store.isPaused ? 'paused' : 'running'
})
const stepIndex = computed(() => (store.currentIndex ?? -1) + 1)
const stepTotal = computed(() => store.actions.length)
const stepLabel = computed(() => {
  if (!activeAction.value) return null
  return activeAction.value.text ?? 'Working on the next step...'
})
const latestFailureMessage = computed(() => {
  for (let index = store.actionStatus.length - 1; index >= 0; index -= 1) {
    if (store.actionStatus[index] !== 'failed') continue
    const message = store.actionErrors[index]
    if (!message) continue
    return `Step ${index + 1} failed: ${message}`
  }
  return null
})

const refreshProgressAnimation = () => {
  if (!showStepProgress.value || !activeAction.value) {
    progressDurationMs.value = null
    return
  }
  progressDurationMs.value = store.getActionDuration(activeAction.value)
  progressAnimationKey.value += 1
}

watch(
  () => [store.currentIndex, activeAction.value?.type, hideOverlayControls.value] as const,
  () => {
    refreshProgressAnimation()
  },
  { immediate: true }
)
</script>

<template>
  <div
    v-if="activeAction && !suppressSingleStepGuidedOverlay"
    class="pointer-events-none fixed inset-0 z-[120]"
  >
    <div
      v-if="targetRect && activeAction.type === 'highlight'"
      class="border-primary absolute rounded-lg border-2 shadow-lg"
      :style="{
        top: `${targetRect.top}px`,
        left: `${targetRect.left}px`,
        width: `${targetRect.width}px`,
        height: `${targetRect.height}px`
      }"
    ></div>

    <div
      v-if="targetRect && activeAction.type === 'spotlight'"
      class="border-primary absolute rounded-lg border-2"
      :style="{
        top: `${targetRect.top}px`,
        left: `${targetRect.left}px`,
        width: `${targetRect.width}px`,
        height: `${targetRect.height}px`,
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)'
      }"
    ></div>

    <div
      v-if="activeAction.text"
      class="bg-background absolute max-w-xs rounded-lg border px-4 py-3 text-sm shadow-lg"
      :style="tooltipStyle ?? undefined"
    >
      {{ activeAction.text }}
    </div>

    <button
      v-if="isUiLocked"
      data-tutorial-id="tutorial.overlay.lock"
      class="pointer-events-none absolute inset-0 block bg-transparent"
      type="button"
      @click.stop
      @mousedown.stop
      @mouseup.stop
    ></button>

    <div
      v-if="!hideOverlayControls"
      ref="actionPanelRef"
      class="bg-background/95 pointer-events-auto absolute max-w-xs rounded-lg border px-4 py-3 text-xs shadow-lg transition-opacity duration-200 hover:opacity-100"
      :style="actionPanelStyle ?? undefined"
      :class="activeAction ? 'opacity-80' : 'opacity-0'"
    >
      <div class="text-muted-foreground mb-2 flex items-center justify-between">
        <span>Step {{ stepIndex }} of {{ stepTotal }}</span>
      </div>
      <div class="text-foreground mb-2 text-sm">{{ stepLabel }}</div>
      <div
        v-if="latestFailureMessage"
        class="mb-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-700"
      >
        {{ latestFailureMessage }}
      </div>
      <div
        v-if="showStepProgress && progressDurationMs"
        class="mb-2 h-0.5 w-full overflow-hidden rounded bg-yellow-200/50"
      >
        <div
          :key="progressAnimationKey"
          class="tutorial-progress-line h-full w-full origin-left bg-yellow-400"
          :style="{
            animationDuration: `${progressDurationMs}ms`,
            animationPlayState: progressAnimationState
          }"
        ></div>
      </div>
      <div class="grid grid-cols-3 gap-2">
        <button
          class="border-input bg-background hover:bg-accent hover:text-accent-foreground inline-flex h-7 items-center justify-center rounded-md border px-2 text-xs"
          type="button"
          @click="store.previousStep()"
        >
          Previous
        </button>
        <button
          class="border-input bg-background hover:bg-accent hover:text-accent-foreground inline-flex h-7 items-center justify-center rounded-md border px-2 text-xs"
          type="button"
          @click="store.nextStep()"
        >
          Next
        </button>
        <button
          class="border-input bg-background hover:bg-accent hover:text-accent-foreground inline-flex h-7 items-center justify-center rounded-md border px-2 text-xs"
          type="button"
          @click="store.stop()"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes tutorial-progress-line {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

.tutorial-progress-line {
  animation-name: tutorial-progress-line;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}
</style>
